import { useState } from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'

import LabeledInput from 'modules/core/components/LabeledInput'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import DocumentTemplatesSelector from 'modules/documents/components/DocumentTemplates/DocumentTemplatesSelector'
import {
  DocumentTemplate,
  useGetDocumentTemplates,
} from 'modules/documents/hooks/useGetDocumentTemplates'
import { ESIGNATURE_MAX_FILE_SIZE } from 'modules/documents/lib/constants'
import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import { useValidationsProvider } from 'modules/processes/components/ItemWizard/Providers/validations'
import { useTaskWizard } from 'modules/processes/components/ItemWizard/Tasks/TaskProvider'

import { useCanManageDocumentTemplates } from './canManageDocumentTemplates'

const SendDocumentTemplate = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | undefined>(undefined)

  const { setTaskConfiguration } = useTaskWizard()
  const { setPreviewValues } = usePreviewProvider()
  const { form } = useFormProvider()
  const { setFieldValidation, removeFieldValidation } = useValidationsProvider()
  const [documentTemplateId, setDocumentTemplateId] = useState<number | null | undefined>()
  const { documentTemplates } = useGetDocumentTemplates()

  const currentTaskConfiguration = form.get('task_type_configuration')?.value

  const updateTaskConfiguration = (newValue: number | null | undefined) => {
    setDocumentTemplateId(newValue)
  }

  const documentTemplatesDisabled = !useCanManageDocumentTemplates()

  useEffect(() => {
    documentTemplatesDisabled && updateTaskConfiguration(null)
  }, [documentTemplatesDisabled])

  useEffect(() => {
    if (documentTemplateId) {
      setTaskConfiguration({
        document_template_id: documentTemplateId,
      })

      setPreviewValues((prevValues) => {
        return {
          ...prevValues,
          documentTemplateName: documentTemplates.find(
            (template) => template.id === documentTemplateId
          )?.name,
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTemplateId])

  useEffect(() => {
    if (currentTaskConfiguration?.document_template_id) {
      setDocumentTemplateId(currentTaskConfiguration?.document_template_id)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTaskConfiguration])

  useEffect(() => {
    setFieldValidation({
      field: 'document_template_id',
      validation: () => !!documentTemplateId,
    })

    return () => {
      removeFieldValidation('document_template_id')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTemplateId])

  const updateTaskConfigurationForTemplate = (template: DocumentTemplate) => {
    setSelectedTemplate(template)
    updateTaskConfiguration(template.id)
  }

  const warningSize =
    selectedTemplate?.fileSize &&
    selectedTemplate.fileSize > ESIGNATURE_MAX_FILE_SIZE &&
    selectedTemplate

  return (
    <Box gap="s8">
      {documentTemplatesDisabled && (
        <Alert color="warning">
          <Text.Normal>
            {i18n.t(
              'processes.process_settings.tasks.form.super_tasks_module.send_document.no_permissions'
            )}
          </Text.Normal>
        </Alert>
      )}

      <LabeledInput
        mandatory
        isLegend
        label={i18n.t('split_documents.templates.create_modal.templates')}
        value={documentTemplateId ? String(documentTemplateId) : undefined}
      >
        <DocumentTemplatesSelector
          documentTemplates={documentTemplates}
          disabled={documentTemplatesDisabled}
          value={documentTemplateId}
          onSelect={updateTaskConfigurationForTemplate}
        />
      </LabeledInput>

      {warningSize && (
        <Alert color="warning">
          <Text.Normal size="300" color="grey1000">
            {i18n.t('documents.beta.file_errors.max_file_size_esignature')}
          </Text.Normal>
        </Alert>
      )}
    </Box>
  )
}

export default SendDocumentTemplate
